import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError, money, fmtDate, fmtHours } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import StatusFiltro, { STATUS_FILTRO_DEFAULT } from "../components/StatusFiltro";
import { toast } from "sonner";
import {
  Plus, Search, Pencil, Eye, FileDown, Mail, Filter, Users, UserCheck, UserX,
  CircleDollarSign, ChevronLeft, ChevronRight, X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { onlyDigits, docType, isValidCNPJ, isValidCPF, isValidDocumento, maskDocumento } from "../lib/documento";
import { useDirty } from "../hooks/useDirty";
import ConfirmCloseDialog from "../components/ConfirmCloseDialog";

// Redesenho da listagem (delta 2026-09-16) — só a LISTA muda (cards de
// resumo, busca/filtros client-side, tabela reorganizada, paginação);
// formulário de cadastro/edição e o dialog de Histórico permanecem
// exatamente como já homologados, mesmos handlers/permissões
// (`canEdit`), mesmo endpoint `/clients`.
const PAGE_SIZE_OPCOES = [10, 25, 50];

const empty = {
  razao_social: "", nome_fantasia: "", cpf_cnpj: "", email: "", telefone: "", whatsapp: "", contato: "",
  cep: "", endereco: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "",
  valor_hora: 0, horas_contratadas: 0, observacoes: "", active: true,
};

// Mesmas traduções usadas em Fichas.jsx/Dashboard.jsx/Relatorios.jsx — usuário
// nunca deve ver o slug técnico do status (ex.: "rejeitada_supervisor") aqui.
const statusPt = (s) => ({
  em_andamento: "Em andamento", concluida: "Concluída",
  rejeitada_supervisor: "Rejeitada pelo supervisor", rejeitada_cliente: "Rejeitada pelo cliente",
  aguardando_aprovacao: "Aguardando aprovação",
  aguardando_assinatura: "Aguardando assinatura",
  aguardando_faturamento: "Aguardando faturamento", faturada: "Faturada", cancelada: "Cancelada",
}[s] || s);

export default function Clientes() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "supervisor";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [hist, setHist] = useState(null);
  const [cnpjLoading, setCnpjLoading] = useState(false);

  // Busca/filtros (delta de layout, 2026-09-16) — client-side sobre a
  // MESMA lista já carregada por /clients (endpoint inalterado; a lista
  // de um tenant nunca é grande o bastante para justificar um novo
  // endpoint paginado/filtrado). q cobre nome/razão social/nome
  // fantasia/código/CPF-CNPJ/cidade — o backend só busca
  // nome/razão/código/CPF-CNPJ (índice cego), então "cidade" só é
  // possível filtrando aqui, sobre o que já veio decriptado do backend.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [statusFiltro, setStatusFiltro] = useState(STATUS_FILTRO_DEFAULT);
  const [cidadeFiltro, setCidadeFiltro] = useState("todas");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPCOES[0]);

  const load = async () => {
    try {
      const { data } = await api.get("/clients");
      setItems(data);
    } catch { /* Correções de homologação (P1) — nunca deixar a promise sem tratamento */ }
  };
  useEffect(() => { load(); }, []);

  // Ajuste de homologação — Ciclo correto da cobrança, item 3: atalho
  // "Editar cliente" vindo de Faturamento (cadastro incompleto bloqueou a
  // emissão) abre direto o formulário de edição via ?edit=<id>, sem exigir
  // que o usuário procure o cliente na lista. Consome o parâmetro uma vez
  // (removido da URL) — nunca reabre o formulário sozinho depois disso.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || !items.length) return;
    const alvo = items.find(c => c.id === editId);
    if (alvo) openEdit(alvo);
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Item 1 — cards de resumo calculados sobre os dados REAIS já
  // carregados (nunca uma métrica inventada/comparação com mês anterior,
  // já que o backend não guarda histórico de contagem de clientes).
  const stats = useMemo(() => {
    const total = items.length;
    const ativos = items.filter(c => c.active).length;
    const comValorHora = items.filter(c => Number(c.valor_hora) > 0);
    const valorHoraMedio = comValorHora.length
      ? comValorHora.reduce((acc, c) => acc + Number(c.valor_hora || 0), 0) / comValorHora.length
      : 0;
    return { total, ativos, inativos: total - ativos, valorHoraMedio };
  }, [items]);

  const cidadesDisponiveis = useMemo(() => {
    const set = new Set();
    items.forEach(c => { if (c.cidade) set.add(`${c.cidade}${c.estado ? `/${c.estado}` : ""}`); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [items]);

  const filteredItems = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter(c => {
      if (statusFiltro === "ativos" && !c.active) return false;
      if (statusFiltro === "inativos" && c.active) return false;
      if (cidadeFiltro !== "todas" && `${c.cidade || ""}${c.estado ? `/${c.estado}` : ""}` !== cidadeFiltro) return false;
      if (!term) return true;
      const alvo = [c.codigo, c.razao_social, c.nome_fantasia, c.cpf_cnpj, c.cidade]
        .filter(Boolean).join(" ").toLowerCase();
      return alvo.includes(term);
    });
  }, [items, q, statusFiltro, cidadeFiltro]);

  // Reinicia a paginação sempre que busca/filtros mudam — nunca deixa o
  // usuário "preso" numa página que deixou de existir.
  useEffect(() => { setPage(1); }, [q, statusFiltro, cidadeFiltro, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const pageAtual = Math.min(page, totalPages);
  const pageItems = filteredItems.slice((pageAtual - 1) * pageSize, pageAtual * pageSize);

  const limparFiltros = () => { setStatusFiltro(STATUS_FILTRO_DEFAULT); setCidadeFiltro("todas"); };
  const filtrosAtivos = statusFiltro !== STATUS_FILTRO_DEFAULT || cidadeFiltro !== "todas";

  const submit = async () => {
    try {
      if (editing) await api.put(`/clients/${editing.id}`, form);
      else await api.post("/clients", form);
      toast.success("Cliente salvo");
      closeForm(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const closeForm = () => { setOpen(false); setForm(empty); setEditing(null); };
  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c }); setEditing(c); setOpen(true); };

  // Proteção contra perda de preenchimento (item 12/13) — mesmo componente/
  // hook já usado em Fichas.jsx, nunca uma segunda implementação. Formulário
  // totalmente vazio (novo cliente ainda não tocado) fecha normalmente —
  // "sujo" só depois de uma alteração real em relação ao que existia ao abrir.
  const dirty = useDirty(open, form);
  const [confirmCloseClienteOpen, setConfirmCloseClienteOpen] = useState(false);
  const requestCloseForm = () => { if (dirty) setConfirmCloseClienteOpen(true); else closeForm(); };

  const buscarCep = async (cep) => {
    const clean = String(cep || "").replace(/\D/g, "");
    if (clean.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const d = await r.json();
      if (!d.erro) {
        setForm(f => ({ ...f, endereco: d.logradouro || "", bairro: d.bairro || "", cidade: d.localidade || "", estado: d.uf || "" }));
      }
    } catch {}
  };

  const openHistory = async (c) => {
    const { data } = await api.get(`/clients/${c.id}/history`);
    setHist({ cliente: c, ...data });
  };

  // Ajustes Finais de Acabamento da V1, item 1 — consulta automática por
  // CNPJ, reaproveitando a mesma infraestrutura já homologada no cadastro
  // de Empresas (endpoint /clients/cnpj-lookup/{cnpj} chama internamente a
  // mesma _lookup_cnpj_brasilapi do backend). Diferente de Empresas.jsx, a
  // precedência aqui protege dados já digitados pelo usuário: um campo só é
  // preenchido pela consulta se ainda estiver vazio no formulário — nunca
  // sobrescreve silenciosamente algo que o usuário já informou.
  const buscarCnpj = async () => {
    const clean = onlyDigits(form.cpf_cnpj);
    if (docType(form.cpf_cnpj) !== "cnpj") return; // botão só aparece para CNPJ; defesa extra
    setCnpjLoading(true);
    try {
      const { data } = await api.get(`/clients/cnpj-lookup/${clean}`);
      setForm(f => ({
        ...f,
        razao_social: f.razao_social.trim() ? f.razao_social : (data.razao_social || ""),
        nome_fantasia: f.nome_fantasia.trim() ? f.nome_fantasia : (data.nome_fantasia || ""),
        email: f.email.trim() ? f.email : (data.email || ""),
        telefone: f.telefone.trim() ? f.telefone : (data.telefone || ""),
        cep: f.cep.trim() ? f.cep : (data.cep || ""),
        endereco: f.endereco.trim() ? f.endereco : (data.logradouro || ""),
        numero: f.numero.trim() ? f.numero : (data.numero || ""),
        complemento: f.complemento.trim() ? f.complemento : (data.complemento || ""),
        bairro: f.bairro.trim() ? f.bairro : (data.bairro || ""),
        cidade: f.cidade.trim() ? f.cidade : (data.municipio || ""),
        estado: f.estado.trim() ? f.estado : (data.uf || ""),
      }));
      toast.success("Dados do CNPJ preenchidos — revise antes de salvar");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCnpjLoading(false); }
  };

  // Mesmo cálculo derivado usado em Empresas.jsx (tipoDoc/docPreenchido/
  // docInvalido/docIncompleto) — reaproveitado aqui para que o campo
  // CPF/CNPJ tenha exatamente o mesmo comportamento visual (máscara ao
  // digitar, borda vermelha quando inválido, texto de validação) nas duas
  // telas, em vez de duas implementações visuais diferentes para o mesmo padrão.
  const tipoDoc = docType(form.cpf_cnpj);
  const docPreenchido = onlyDigits(form.cpf_cnpj).length > 0;
  const docInvalido = docPreenchido && tipoDoc !== null && !isValidDocumento(form.cpf_cnpj);
  const docIncompleto = docPreenchido && tipoDoc === null;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Clientes</h1>
          <p className="text-slate-500 text-sm">Cadastro e histórico dos clientes</p>
        </div>
        {canEdit && (
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={openNew} data-testid="clientes-novo">
            <Plus size={16} /> Novo cliente
          </Button>
        )}
      </div>

      {/* Item 1 — cards de resumo, calculados sobre os dados reais já carregados */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-500">Total de clientes</div>
              <div className="font-heading font-bold text-2xl text-slate-900">{stats.total}</div>
            </div>
            <div className="rounded-full bg-slate-100 p-2 text-slate-500"><Users size={18} /></div>
          </div>
        </Card>
        <Card className="p-4 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-500">Clientes ativos</div>
              <div className="font-heading font-bold text-2xl text-emerald-700">{stats.ativos}</div>
            </div>
            <div className="rounded-full bg-emerald-100 p-2 text-emerald-700"><UserCheck size={18} /></div>
          </div>
        </Card>
        <Card className="p-4 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-500">Clientes inativos</div>
              <div className="font-heading font-bold text-2xl text-slate-500">{stats.inativos}</div>
            </div>
            <div className="rounded-full bg-slate-100 p-2 text-slate-500"><UserX size={18} /></div>
          </div>
        </Card>
        <Card className="p-4 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-500">Valor/hora médio</div>
              <div className="font-heading font-bold text-2xl text-slate-900">{money(stats.valorHoraMedio)}</div>
            </div>
            <div className="rounded-full bg-slate-100 p-2 text-slate-500"><CircleDollarSign size={18} /></div>
          </div>
        </Card>
      </div>

      {/* Item 2 — busca + filtros, tudo client-side sobre a lista já carregada */}
      <Card className="p-4 rounded-2xl shadow-sm space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={16} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              data-testid="clientes-search" className="pl-8"
              value={q} onChange={e => setQ(e.target.value)}
              placeholder="Buscar por nome, razão social, CPF/CNPJ, código ou cidade"
            />
          </div>
          <Button variant={filtrosAtivos ? "default" : "outline"} onClick={() => setFiltersOpen(v => !v)} data-testid="clientes-filtros-toggle">
            <Filter size={16} /> Filtros{filtrosAtivos ? " •" : ""}
          </Button>
        </div>
        {filtersOpen && (
          <div className="flex flex-wrap gap-3 items-end pt-2 border-t border-slate-100">
            <StatusFiltro value={statusFiltro} onChange={setStatusFiltro} testId="clientes-filtro-status" />
            {cidadesDisponiveis.length > 0 && (
              <div className="min-w-[200px]">
                <Label className="text-xs">Cidade/UF</Label>
                <Select value={cidadeFiltro} onValueChange={setCidadeFiltro}>
                  <SelectTrigger data-testid="clientes-filtro-cidade"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas</SelectItem>
                    {cidadesDisponiveis.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {filtrosAtivos && (
              <Button variant="ghost" size="sm" onClick={limparFiltros} data-testid="clientes-filtro-limpar">
                <X size={14} /> Limpar filtros
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Item 3 — tabela reorganizada. "Última movimentação" foi OMITIDA
          nesta primeira versão: o backend/modelo de Cliente não guarda
          nenhum campo confiável de última movimentação hoje — nunca
          inventamos uma data. */}
      <Card className="p-0 overflow-hidden rounded-2xl shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="py-3 px-4">Código</th><th>Razão Social</th><th>CPF/CNPJ</th>
                <th>Cidade/UF</th><th>Valor hora</th><th>Situação</th><th className="text-right pr-4">Ações</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map(c => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 font-medium">{c.codigo}</td>
                  <td>
                    <div className="font-medium text-slate-900">{c.razao_social}</div>
                    <div className="text-xs text-slate-500">{c.nome_fantasia}</div>
                  </td>
                  <td>{c.cpf_cnpj}</td>
                  <td>{c.cidade}{c.estado ? `/${c.estado}` : ""}</td>
                  <td>{money(c.valor_hora)}</td>
                  <td><Badge className={c.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}>{c.active ? "Ativo" : "Inativo"}</Badge></td>
                  <td className="text-right pr-4">
                    <Button size="sm" variant="ghost" title="Visualizar" onClick={() => openHistory(c)} data-testid={`cli-hist-${c.codigo}`}><Eye size={16} /></Button>
                    {canEdit && <Button size="sm" variant="ghost" title="Editar" onClick={() => openEdit(c)} data-testid={`cli-edit-${c.codigo}`}><Pencil size={16} /></Button>}
                  </td>
                </tr>
              ))}
              {pageItems.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-slate-400">
                  {items.length === 0 ? "Nenhum cliente cadastrado" : "Nenhum cliente encontrado para os filtros atuais"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Item 5 — paginação (client-side; /clients continua devolvendo
            a lista inteira do tenant, sem alteração de API). */}
        {filteredItems.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <span>Itens por página</span>
              <Select value={String(pageSize)} onValueChange={v => setPageSize(Number(v))}>
                <SelectTrigger className="w-20 h-8" data-testid="clientes-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPCOES.map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span data-testid="clientes-page-info">
                Página {pageAtual} de {totalPages} — {filteredItems.length} cliente{filteredItems.length === 1 ? "" : "s"}
              </span>
              <Button size="sm" variant="outline" disabled={pageAtual <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="clientes-page-prev">
                <ChevronLeft size={16} />
              </Button>
              <Button size="sm" variant="outline" disabled={pageAtual >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="clientes-page-next">
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Item 9/11/12 — CPF/CNPJ é o primeiro campo (foco lógico do
          cadastro); modal grande, organizado em seções (Identificação/
          Contato/Endereço/Comercial) para reduzir rolagem; backdrop
          não-dismissable, ESC/X/Cancelar com alteração pendente pedem
          confirmação (mesmo componente/hook de Fichas.jsx, item 13). */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) requestCloseForm(); }}>
        <DialogContent
          className="w-[95vw] max-w-[1100px] max-h-[92vh] flex flex-col"
          onPointerDownOutside={e => e.preventDefault()}
          onInteractOutside={e => e.preventDefault()}
          onEscapeKeyDown={e => { e.preventDefault(); requestCloseForm(); }}
        >
          <DialogHeader className="shrink-0"><DialogTitle>{editing ? "Editar cliente" : "Novo cliente"}</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto pr-1 space-y-5">
            <section>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Identificação</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <Label>CPF/CNPJ *</Label>
                  <div className="flex flex-wrap gap-2">
                    <Input
                      autoFocus
                      value={form.cpf_cnpj}
                      onChange={e => setForm({ ...form, cpf_cnpj: maskDocumento(e.target.value) })}
                      placeholder="Digite o CPF ou CNPJ"
                      className={`min-w-0 flex-1 ${docInvalido ? "border-red-400" : ""}`}
                      data-testid="cliente-form-cpf-cnpj"
                    />
                    {tipoDoc === "cnpj" && isValidCNPJ(form.cpf_cnpj) && (
                      <Button type="button" variant="outline" onClick={buscarCnpj} disabled={cnpjLoading} className="shrink-0" data-testid="cliente-form-buscar-cnpj">
                        <Search size={16} /> {cnpjLoading ? "Buscando..." : "Buscar dados"}
                      </Button>
                    )}
                  </div>
                  {docPreenchido && (
                    <p className={`text-xs mt-1 ${docInvalido || docIncompleto ? "text-red-500" : "text-emerald-600"}`}>
                      {tipoDoc === "cpf" && (isValidCPF(form.cpf_cnpj) ? "CPF válido — consulta automática não se aplica a pessoa física" : "CPF inválido")}
                      {tipoDoc === "cnpj" && (isValidCNPJ(form.cpf_cnpj) ? "CNPJ válido" : "CNPJ inválido")}
                      {docIncompleto && "Documento incompleto — informe um CPF (11 dígitos) ou CNPJ (14 dígitos)"}
                    </p>
                  )}
                </div>
                <div><Label>Razão Social *</Label><Input data-testid="form-razao" value={form.razao_social} onChange={e => setForm({ ...form, razao_social: e.target.value })} /></div>
                <div><Label>Nome Fantasia</Label><Input value={form.nome_fantasia} onChange={e => setForm({ ...form, nome_fantasia: e.target.value })} /></div>
              </div>
            </section>

            <section>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Contato</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div><Label>E-mail</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Telefone</Label><Input value={form.telefone} onChange={e => setForm({ ...form, telefone: e.target.value })} /></div>
                {/* Integração WhatsApp (Fase 1) — campo dedicado, usado como
                    prioridade 2 na resolução do destinatário do link de
                    assinatura quando a ficha não tem WhatsApp próprio. */}
                <div><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} placeholder="(00) 00000-0000" data-testid="cliente-form-whatsapp" /></div>
                <div><Label>Pessoa de contato</Label><Input value={form.contato} onChange={e => setForm({ ...form, contato: e.target.value })} /></div>
              </div>
            </section>

            <section>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Endereço</div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div><Label>CEP</Label><Input value={form.cep} onChange={e => setForm({ ...form, cep: e.target.value })} onBlur={e => buscarCep(e.target.value)} placeholder="00000-000" /></div>
                <div className="col-span-2 lg:col-span-2"><Label>Endereço</Label><Input value={form.endereco} onChange={e => setForm({ ...form, endereco: e.target.value })} /></div>
                <div><Label>Número</Label><Input value={form.numero} onChange={e => setForm({ ...form, numero: e.target.value })} /></div>
                <div><Label>Complemento</Label><Input value={form.complemento} onChange={e => setForm({ ...form, complemento: e.target.value })} /></div>
                <div><Label>Bairro</Label><Input value={form.bairro} onChange={e => setForm({ ...form, bairro: e.target.value })} /></div>
                <div><Label>Cidade</Label><Input value={form.cidade} onChange={e => setForm({ ...form, cidade: e.target.value })} /></div>
                <div><Label>Estado (UF)</Label><Input value={form.estado} onChange={e => setForm({ ...form, estado: e.target.value })} /></div>
              </div>
            </section>

            <section>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Comercial</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><Label>Valor da hora (R$)</Label><Input type="number" step="0.01" value={form.valor_hora} onChange={e => setForm({ ...form, valor_hora: parseFloat(e.target.value) || 0 })} data-testid="form-valor-hora" /></div>
                <div><Label>Horas contratadas</Label><Input type="number" step="0.5" value={form.horas_contratadas} onChange={e => setForm({ ...form, horas_contratadas: parseFloat(e.target.value) || 0 })} /></div>
                <div className="flex items-end pb-2"><div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} /><Label>Ativo</Label></div></div>
                <div className="md:col-span-3"><Label>Observações</Label><Input value={form.observacoes} onChange={e => setForm({ ...form, observacoes: e.target.value })} /></div>
              </div>
            </section>
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={requestCloseForm} data-testid="form-cancelar-cliente">Cancelar</Button>
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={submit} data-testid="form-salvar-cliente">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmCloseDialog
        open={confirmCloseClienteOpen} onOpenChange={setConfirmCloseClienteOpen}
        onConfirm={() => { setConfirmCloseClienteOpen(false); closeForm(); }}
        title="Descartar cadastro?"
        description="Você preencheu dados deste cliente e ainda não salvou. Se sair agora, o preenchimento será perdido."
      />

      <Dialog open={!!hist} onOpenChange={v => !v && setHist(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Histórico — {hist?.cliente?.razao_social}</DialogTitle></DialogHeader>
          {hist && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-3"><div className="text-xs text-slate-500">Fichas</div><div className="font-heading font-bold text-xl">{hist.totais.qtd_tickets}</div></Card>
                <Card className="p-3"><div className="text-xs text-slate-500">Horas realizadas</div><div className="font-heading font-bold text-xl">{fmtHours(hist.totais.horas_realizadas)}</div></Card>
                <Card className="p-3"><div className="text-xs text-slate-500">Horas faturadas</div><div className="font-heading font-bold text-xl">{fmtHours(hist.totais.horas_faturadas)}</div></Card>
                <Card className="p-3"><div className="text-xs text-slate-500">Valor faturado</div><div className="font-heading font-bold text-xl">{money(hist.totais.valor_faturado)}</div></Card>
              </div>
              <div>
                <div className="font-heading font-semibold mb-2">Fichas</div>
                <table className="w-full text-sm">
                  <thead className="text-left text-slate-500 border-b"><tr><th className="py-2">#</th><th>Data</th><th>Serviço</th><th>Horas</th><th>Valor</th><th>Status</th></tr></thead>
                  <tbody>
                    {hist.tickets.map(t => (
                      <tr key={t.id} className="border-b border-slate-100">
                        <td className="py-2">{t.numero}</td><td>{fmtDate(t.data_servico)}</td><td>{t.tipo_servico}</td>
                        <td>{fmtHours(t.horas_realizadas)}</td><td>{money(t.valor_total)}</td><td>{statusPt(t.status)}</td>
                      </tr>
                    ))}
                    {hist.tickets.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-slate-400">Sem fichas</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
